import axios from 'axios';

export const INSIGHT_DATA_CHANNEL = 'insight-data-changes-v1';
export const INSIGHT_DATA_STORAGE_KEY = 'insight_data_change_v1';
export const INSIGHT_DATA_EVENT = 'insight:data-change';

const ALLOWED_TYPES = new Set([
  'report_visibility_changed',
  'session_data_changed',
  'phase5_report_changed',
  'client_feedback_changed',
  'report_review_changed',
  'report_share_changed',
]);

// Deliberately construct a new allow-listed object. Mutation response bodies and
// request bodies must never enter BroadcastChannel/localStorage.
export const safeInvalidation = ({ type, sessionId, reportKey } = {}) => ({
  type: ALLOWED_TYPES.has(type) ? type : 'session_data_changed',
  ...(sessionId ? { sessionId: String(sessionId) } : {}),
  ...(reportKey ? { reportKey: String(reportKey).slice(0, 80) } : {}),
  timestamp: Date.now(),
});

export const publishInsightInvalidation = (change) => {
  if (typeof window === 'undefined') return;
  const event = safeInvalidation(change);
  window.dispatchEvent(new CustomEvent(INSIGHT_DATA_EVENT, { detail: event }));
  if (typeof window.BroadcastChannel === 'function') {
    const channel = new window.BroadcastChannel(INSIGHT_DATA_CHANNEL);
    channel.postMessage(event);
    channel.close();
  } else {
    try { window.localStorage.setItem(INSIGHT_DATA_STORAGE_KEY, JSON.stringify(event)); } catch (_) { /* storage may be disabled */ }
  }
};

export const subscribeInsightInvalidation = (listener) => {
  if (typeof window === 'undefined') return () => {};
  const onLocal = (event) => listener(safeInvalidation(event.detail));
  const onStorage = (event) => {
    if (event.key !== INSIGHT_DATA_STORAGE_KEY || !event.newValue) return;
    try { listener(safeInvalidation(JSON.parse(event.newValue))); } catch (_) { /* ignore invalid external storage */ }
  };
  let channel;
  if (typeof window.BroadcastChannel === 'function') {
    channel = new window.BroadcastChannel(INSIGHT_DATA_CHANNEL);
    channel.onmessage = (event) => listener(safeInvalidation(event.data));
  }
  window.addEventListener(INSIGHT_DATA_EVENT, onLocal);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(INSIGHT_DATA_EVENT, onLocal);
    window.removeEventListener('storage', onStorage);
    channel?.close();
  };
};

export const classifyMutation = (config = {}) => {
  const method = String(config.method || 'get').toLowerCase();
  if (!['post', 'put', 'patch', 'delete'].includes(method)) return null;
  const url = String(config.url || '');
  if (/\/auth\//.test(url)) return null;
  const sessionId = url.match(/\/sessions\/([^/?]+)/)?.[1];
  let type = 'session_data_changed';
  if (/\/admin\/settings/.test(url) && config.data && String(config.data).includes('report_visibility')) type = 'report_visibility_changed';
  else if (/\/report-visibility/.test(url)) type = 'report_share_changed';
  else if (/submit-client-feedback|\/feedback(?:[/?]|$)/.test(url)) type = 'client_feedback_changed';
  else if (/\/review(?:[/?]|$)/.test(url)) type = 'report_review_changed';
  else if (/\/share(?:[/?]|$)/.test(url)) type = 'report_share_changed';
  else if (/phase5|reports?|recommendations|queries/.test(url)) type = 'phase5_report_changed';
  return safeInvalidation({ type, sessionId });
};

let interceptorId;
export const installInsightMutationInvalidation = () => {
  if (interceptorId !== undefined) return interceptorId;
  interceptorId = axios.interceptors.response.use((response) => {
    const change = classifyMutation(response.config);
    if (change) publishInsightInvalidation(change);
    return response;
  });
  return interceptorId;
};
